import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Award, BadgeCheck } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { FOUNDER, DASHBOARDS } from "../data/content";

const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="about" className="relative py-24 sm:py-32 lg:py-40" data-testid="about-section">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-8 lg:px-12">
        {/* Company profile */}
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-24">
          <Reveal>
            <Eyebrow>03 — About Us</Eyebrow>
            <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              A UK technology company building intelligent platforms.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="flex flex-col justify-center gap-6">
            <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
              TechSpace Solutions Ltd is a UK technology company specialising in enterprise software
              development, Microsoft technologies, artificial intelligence and cloud-based digital
              transformation.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              We help organisations improve operational efficiency, compliance and decision making
              through innovative software solutions.
            </p>
            <div className="rounded-2xl border border-teal/30 bg-teal/5 p-6">
              <p className="font-display text-lg font-medium text-foreground">
                “Our mission is to build intelligent platforms that simplify complex business
                processes.”
              </p>
            </div>
          </Reveal>
        </div>

        {/* Founder */}
        <div ref={ref} className="mt-28 grid grid-cols-1 gap-12 lg:grid-cols-5 lg:gap-16">
          <Reveal className="lg:col-span-2">
            <div className="relative overflow-hidden rounded-3xl border border-border">
              <div className="pointer-events-none absolute inset-0 z-10 bg-gradient-to-t from-navy/60 via-transparent to-transparent" />
              <motion.img
                src={DASHBOARDS.founder}
                alt={FOUNDER.name}
                style={{ y: imgY }}
                className="h-[520px] w-full scale-105 object-cover object-top"
                loading="lazy"
              />
              <div className="absolute bottom-0 left-0 z-20 p-7">
                <p className="font-display text-2xl font-bold text-white">{FOUNDER.name}</p>
                <p className="text-sm text-white/80">{FOUNDER.role}</p>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {FOUNDER.certifications.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground"
                >
                  <BadgeCheck className="h-3.5 w-3.5 text-teal" />
                  {c}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-3">
            <Eyebrow>The Founder</Eyebrow>
            <h3 className="mt-4 font-display text-2xl font-semibold text-foreground sm:text-3xl">
              Deep Microsoft &amp; enterprise architecture expertise.
            </h3>
            <p className="mt-4 text-muted-foreground">{FOUNDER.summary}</p>

            <div className="mt-6 flex flex-wrap gap-2">
              {FOUNDER.expertise.map((e) => (
                <span
                  key={e}
                  className="rounded-full bg-secondary px-3 py-1.5 text-xs font-medium text-secondary-foreground"
                >
                  {e}
                </span>
              ))}
            </div>

            {/* Timeline */}
            <div className="mt-10">
              <div className="mb-6 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-teal">
                <Award className="h-4 w-4" /> Career Milestones
              </div>
              <div className="relative space-y-6 border-l border-border pl-8">
                {FOUNDER.timeline.map((t, i) => (
                  <motion.div
                    key={t.year}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.5, delay: i * 0.05 }}
                    className="relative"
                    data-testid={`timeline-${t.year}`}
                  >
                    <span className="absolute -left-[38px] top-1 flex h-4 w-4 items-center justify-center rounded-full border-2 border-teal bg-background">
                      <span className="h-1.5 w-1.5 rounded-full bg-teal" />
                    </span>
                    <div className="flex flex-col gap-0.5">
                      <span className="font-display text-sm font-bold text-teal">{t.year}</span>
                      <span className="font-semibold text-foreground">{t.title}</span>
                      <span className="text-sm text-muted-foreground">{t.desc}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default About;
